import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getCategoriesApi,
  getVideoLikesApi,
  postLikeApi,
  postVideoProgressApi,
} from "src/features/tangoAcademy/api/academyApi";
import {
  CategoriesResponse,
  Category,
  ProgressResponse,
  VideoLikesResponse,
} from "src/features/tangoAcademy/common/types";
import {
  LanguageIsoCode,
  RootState,
  batchLoadProfiles,
} from "src/features/tangoAcademy/imports/common";

export const getCategories = createAsyncThunk<
  CategoriesResponse,
  void,
  { rejectValue: string; state: RootState }
>("lwc/tangoAcademy/getCategories", getCategoriesApi, {
  condition: (_, { getState }) => !getState().tangoAcademy.getCategories.data, // Cancel request if data already exists
});

export const getVideoLikes = createAsyncThunk<
  VideoLikesResponse,
  {
    videoId: string;
  },
  { state: RootState }
>(
  "lwc/tangoAcademy/getVideoReactions",
  async ({ videoId }, { getState, dispatch }) => {
    const page = (getState().tangoAcademy.getVideoLikes.data?.page ?? -1) + 1;

    const response = await getVideoLikesApi(videoId, page);
    const accountIds = response.users?.map(({ accountId }) => accountId);

    await dispatch(
      batchLoadProfiles({
        ids: accountIds,
        loadOnlyIfMissing: true,
        params: { basic: true, detail: false, live: true, follow: false },
      })
    );

    return response;
  }
);

export const postVideoProgress = createAsyncThunk<
  ProgressResponse,
  { progressPercent: number; videoId: string },
  { state: RootState }
>("tangoAcademy/postVideoProgress", async ({ videoId, progressPercent }) =>
  postVideoProgressApi(videoId, progressPercent)
);

type RequestState<T = unknown> =
  | { data: T; error: null; status: "succeeded" }
  | { data: null; error: null; status: "idle" }
  | { data: null; error: null; status: "loading" }
  | { data: null; error: string; status: "failed" };

export type TangoAcademyState = {
  captions: {
    languageCode: LanguageIsoCode;
    status: "off" | "on";
  };
  getCategories: RequestState<Category[]>;
  getVideoLikes: RequestState<{ page: number } & VideoLikesResponse>;
};

export const initialRequestState: RequestState = {
  status: "idle",
  data: null,
  error: null,
};

export const initialState = {
  getCategories: initialRequestState,
  getVideoLikes: initialRequestState,
  captions: {
    status: "on",
    languageCode: "en",
  },
} as TangoAcademyState;

const slice = createSlice({
  name: "tangoAcademy",
  initialState,
  reducers: {
    updateLikes: (
      state,
      action: PayloadAction<{ id: string; isLiked: boolean }>
    ) => {
      const { id, isLiked } = action.payload;
      const video = state.getCategories.data
        ?.flatMap((category) => category.videos)
        .find((video) => video.id === id);

      if (video) {
        video.isLiked = isLiked;
        video.likesCount += isLiked ? 1 : -1;
      }
    },
    updateCaptions: (
      state,
      action: PayloadAction<Partial<TangoAcademyState["captions"]>>
    ) => {
      state.captions = { ...state.captions, ...action.payload };
    },
    resetVideoLikes: (state) => {
      state.getVideoLikes = initialRequestState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCategories.pending, (state) => {
      state.getCategories = {
        status: "loading",
        error: null,
        data: null,
      };
    });
    builder.addCase(getCategories.fulfilled, (state, action) => {
      state.getCategories = {
        status: "succeeded",
        data: action.payload.categories,
        error: null,
      };
    });
    builder.addCase(getCategories.rejected, (state, action) => {
      state.getCategories = {
        status: "failed",
        error: action.error.message ?? "Something went wrong",
        data: null,
      };
    });
    builder.addCase(getVideoLikes.pending, (state) => {
      state.getVideoLikes.status = "loading";
    });
    builder.addCase(getVideoLikes.fulfilled, (state, action) => {
      const existingUsers = state.getVideoLikes.data?.users ?? [];
      const page = (state.getVideoLikes.data?.page ?? -1) + 1;
      const users = action.payload.users ?? [];

      state.getVideoLikes = {
        status: "succeeded",
        error: null,
        data: {
          ...action.payload,
          users: [...existingUsers, ...users],
          page,
        },
      };
    });
    builder.addCase(getVideoLikes.rejected, (state, action) => {
      state.getVideoLikes.status = "failed";
      state.getVideoLikes.error =
        action.error.message ?? "Failed to load video reactions";
    });
    builder.addCase(postVideoProgress.fulfilled, (state, action) => {
      const { videoId } = action.meta.arg;
      const { isViewed } = action.payload;
      const video = state.getCategories.data
        ?.flatMap((category) => category.videos)
        .find((video) => video.id === videoId);

      if (video) {
        video.isViewed = isViewed;
      }
    });
  },
});

export const tangoAcademyReducer = slice.reducer;
export const { updateLikes, updateCaptions, resetVideoLikes } = slice.actions;

export const toggleLike = createAsyncThunk<
  void,
  { id: string; isLiked: boolean },
  { state: RootState }
>("tangoAcademy/toggleLike", async ({ id, isLiked }, { dispatch }) => {
  dispatch(updateLikes({ id, isLiked }));

  try {
    await postLikeApi(id, isLiked);
  } catch (error) {
    dispatch(updateLikes({ id, isLiked: !isLiked }));
    throw error;
  }
});
